import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { apiGet, apiPost, apiPut } from '../../../services/apiFetch';
import ErrorAlert from '../../components/ErrorAlert';
import '../../../styles/PlayerList.css';
import '../../../styles/AdminPanel.css';

// Material-UI Date Picker imports
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useAdminData } from '../../../contexts/AdminDataContext';

const PersonIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx="12" cy="12" r="12" fill="#1e293b"/>
    <circle cx="12" cy="9" r="3.5" fill="#475569"/>
    <path d="M4.5 20c0-4.142 3.358-7 7.5-7s7.5 2.858 7.5 7" stroke="#475569" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
  </svg>
);

const PlayerForm = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isEditing = !!id;
  const { refreshPlayers } = useAdminData();
  // Try to grab the passed-in player
  const passedPlayer = location.state?.player;

  const [formData, setFormData] = useState({
    username: '',
    name: '',
    DOB: '',
    image: '',
    country: ''
  });

  const [loading, setLoading] = useState(isEditing && !passedPlayer);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState(null);

  useEffect(() => {
    if (!isEditing || passedPlayer) {
      // If we're creating or we already have the player, no need to fetch
      if (passedPlayer) {
        setFormData({
          username: passedPlayer.username || '',
          name: passedPlayer.name || '',
          DOB: passedPlayer.DOB || '',
          image: passedPlayer.image || '',
          country: passedPlayer.country || ''
        });
      }
      setSubmitting(false);
      return;
    }

    // Otherwise fetch as before
    (async () => {
      try {
        const { data, error } = await apiGet(`/api/rl/players/${id}`);
        if (error) throw new Error(error.error);
        setFormData({
          username: data.username,
          name: data.name || '',
          DOB: data.DOB || '',
          image: data.image || '',
          country: data.country || ''
        });
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEditing, passedPlayer, refreshPlayers]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = "Username is required";
    }

    if (!formData.name.trim()) {
      newErrors.name = "Player name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Clear error when user types
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  // Handle date of birth picker change
  const handleDOBChange = (date) => {
    if (date) {
      const formattedDate = date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
      setFormData({
        ...formData,
        DOB: formattedDate
      });
    } else {
      setFormData({
        ...formData,
        DOB: ''
      });
    }

    // Clear error when user selects a date
    if (errors.DOB) {
      setErrors({
        ...errors,
        DOB: null
      });
    }
  };

  const handleImageChange = (e) => {
    // In a real application, this would handle file uploads
    const file = e.target.files[0];
    if (file) {
      // Mock image URL
      const imageUrl = URL.createObjectURL(file);
      setFormData({
        ...formData,
        image: imageUrl
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setApiError(null);

    if (!validateForm()) {
      setSubmitting(false);
      return;
    }

    try {
      if (isEditing) {
        const { data, error } = await apiPut(`/api/rl/players/${id}`, { ...formData, id: id });
        if (error) {
          setApiError(error);
          return;
        }
        console.log(data)
      } else {
        const { data, error } = await apiPost('/api/rl/players', formData);
        if (error) {
          setApiError(error);
          return;
        }
        console.log(data)
      }
      await refreshPlayers();
      navigate('/admin/players');
    } catch (error) {
      console.error("Error submitting player:", error);
      setApiError({
        error: 'internal_error',
        message: error.message || 'Error submitting player. Please try again.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  if (loading) {
    return <div className="loading-spinner"></div>;
  }

  return (
    <div className="player-form-page">
      <div className="page-header">
        <button type="button" className="back-button" onClick={() => navigate(-1)} style={{marginLeft: 16}}>
          ← Back
        </button>
        <h1>{isEditing ? 'Edit Player' : 'Create Player'}</h1>
      </div>

      <form onSubmit={handleSubmit} className="admin-form">
        <div className="form-section">
          <h2>Player Details</h2>

          <div className="form-row">
            <div className="form-group">
              <label>Username</label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                className={errors.username ? "error-input" : ""}
              />
              {errors.username && <div className="error-message">{errors.username}</div>}
            </div>

            <div className="form-group">
              <label>Player Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={errors.name ? "error-input" : ""}
              />
              {errors.name && <div className="error-message">{errors.name}</div>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Date of Birth</label>
              <div className="date-time-picker-container">
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    value={formData.DOB ? new Date(formData.DOB) : null}
                    onChange={handleDOBChange}
                    maxDate={new Date()} // Can't be born in the future
                    slotProps={{
                      textField: {
                        size: 'small',
                        variant: 'outlined',
                        fullWidth: true,
                        placeholder: 'Select date of birth...'
                      }
                    }}
                  />
                </LocalizationProvider>
                {formData.DOB && (
                  <button
                    type="button"
                    className="picker-clear-btn"
                    onClick={() => handleDOBChange(null)}
                    title="Clear date of birth"
                    aria-label="Clear selected date of birth"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>

            <div className="form-group">
              <label>Country</label>
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Player Image</label>

              <div className="image-preview">
                {formData.image ? (
                  <img
                    src={formData.image}
                    alt={formData.username}
                    onError={(e) => { e.target.src = '/assets/images/placeholder.png' }}
                  />
                ) : (
                  <PersonIcon className="player-image-icon" />
                )}
              </div>

              <div className="file-input">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                />
                <div className="file-input-info">
                  Supported formats: JPEG, PNG. Max size: 2MB
                </div>
              </div>

              {/* For demo purposes, allow direct URL input */}
              <input
                type="text"
                name="image"
                value={formData.image}
                onChange={handleInputChange}
                placeholder="Or enter image URL directly"
                className="mt-2"
              />
            </div>
          </div>
        </div>

        <ErrorAlert 
          error={apiError} 
          onDismiss={() => setApiError(null)}
          title="Error Submitting Player"
        />

        <div className="form-actions">
          <button
            type="button"
            className="cancel-button"
            onClick={handleCancel}
          >
            Cancel
          </button>

          <button
            type="submit"
            className="submit-button"
            disabled={submitting}
          >
            {submitting ? 'Saving...' : (isEditing ? 'Update Player' : 'Create Player')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PlayerForm;
